﻿"use strict";

/**
 * Hero Section GSAP Animations
 * Animates hero elements after preloader finishes
 */

// Global flag to ensure animation only runs once across all instances
let hasAnimatedGlobally = false;
// Global flag to track if initial states have been set
let hasSetInitialStates = false;

function initHeroSection() {
  // If animation has already completed, don't re-initialize
  if (hasAnimatedGlobally) {
    return;
  }

  const heroSection = document.querySelector(".section-hero");
  if (!heroSection) return;

  const isMobile = window.innerWidth <= 999;

  // Get elements
  const heroTitle = heroSection.querySelector(".hero-title");
  const titleTexts = heroTitle ? heroTitle.querySelectorAll("p") : [];
  const heroLeft = heroSection.querySelector(".hero-left");
  const heroMiddle = heroSection.querySelector(".hero-middle");
  const heroRight = heroSection.querySelector(".hero-right");
  const productForm = heroSection.querySelector(".product-form");
  const mainHeader = document.querySelector(".main-header");

  // Check if elements exist
  if (
    !heroTitle ||
    titleTexts.length < 2 ||
    !heroLeft ||
    !heroMiddle ||
    !heroRight ||
    !productForm
  ) {
    return;
  }

  // Check if GSAP is available (needed for both mobile and desktop)
  if (typeof gsap === "undefined") {
    // Wait for GSAP to load
    const checkGSAP = setInterval(() => {
      if (hasAnimatedGlobally) {
        clearInterval(checkGSAP);
        return;
      }
      if (typeof gsap !== "undefined") {
        clearInterval(checkGSAP);
        setInitialStates();
        waitForPreloaderFinish();
      }
    }, 100);

    // Timeout after 5 seconds
    setTimeout(() => {
      if (hasAnimatedGlobally) {
        clearInterval(checkGSAP);
        return;
      }
      clearInterval(checkGSAP);
      if (typeof gsap !== "undefined") {
        setInitialStates();
        waitForPreloaderFinish();
      }
    }, 5000);
  } else {
    // GSAP already available
    setInitialStates();
    waitForPreloaderFinish();
  }

  // ============================================
  // MOBILE ANIMATION FUNCTIONS
  // ============================================
  function setMobileInitialStates() {
    gsap.set(heroTitle, {
      y: "1rem",
      opacity: 0,
    });

    gsap.set(heroLeft, {
      y: "2rem",
      opacity: 0,
    });

    gsap.set(heroMiddle, {
      x: "2rem",
      opacity: 0,
    });

    gsap.set(heroRight, {
      y: "2rem",
      opacity: 0,
    });

    gsap.set(productForm, {
      x: "-2rem",
      opacity: 0,
    });

    if (mainHeader) {
      gsap.set(mainHeader, {
        y: "-100%",
      });
    }
  }

  function animateMobile() {
    const tl = gsap.timeline();

    // Title animation
    tl.to(heroTitle, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power2.out",
    });

    // After title: Form and Middle animate together
    tl.to(
      [productForm, heroMiddle],
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out",
      },
      "-=0.3"
    );

    // Then Left animates
    tl.to(
      heroLeft,
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out",
      },
      "-=0.4"
    );

    // Then Right animates
    tl.to(
      heroRight,
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out",
      },
      "-=0.4"
    );

    // Header animates
    if (mainHeader) {
      tl.to(
        mainHeader,
        {
          y: 0,
          duration: 0.5,
          ease: "power2.out",
        },
        "<"
      );
    }
  }

  // ============================================
  // DESKTOP ANIMATION FUNCTIONS
  // ============================================
  function setDesktopInitialStates() {
    gsap.set(titleTexts[0], {
      x: "-100%",
      opacity: 0,
    });

    gsap.set(titleTexts[1], {
      x: "100%",
      opacity: 0,
    });

    gsap.set(heroLeft, {
      x: "-2rem",
      opacity: 0,
    });

    gsap.set(heroMiddle, {
      x: "2rem",
      opacity: 0,
    });

    gsap.set(heroRight, {
      x: "2rem",
      opacity: 0,
    });

    gsap.set(productForm, {
      x: "-2rem",
      opacity: 0,
    });

    if (mainHeader) {
      gsap.set(mainHeader, {
        y: "-100%",
      });
    }
  }

  function animateDesktop() {
    const tl = gsap.timeline();

    // Title texts animate together at the same time
    tl.to(titleTexts, {
      x: 0,
      opacity: 1,
      duration: 0.8,
      ease: "power3.out",
    });

    // After title: Form and Middle animate together
    tl.to(
      [productForm, heroMiddle],
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out",
      },
      "-=0.5"
    );

    // Then Left animates
    tl.to(
      heroLeft,
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out",
      },
      "-=0.4"
    );

    // Then Right animates
    tl.to(
      heroRight,
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out",
      },
      "-=0.8"
    );

    if (mainHeader) {
      tl.to(
        mainHeader,
        {
          y: 0,
          duration: 0.8,
          ease: "power3.out",
        },
        "<"
      );
    }
  }

  // ============================================
  // MAIN ANIMATION FUNCTIONS
  // ============================================
  function setInitialStates() {
    // Don't set initial states if animation has already run or states already set
    if (hasAnimatedGlobally || hasSetInitialStates) {
      return;
    }

    if (typeof gsap === "undefined") {
      return; // Don't set initial states if GSAP is not available
    }

    hasSetInitialStates = true;

    // Call appropriate function based on device type
    if (isMobile) {
      setMobileInitialStates();
    } else {
      setDesktopInitialStates();
    }
  }

  function animateAfterPreloader() {
    if (typeof gsap === "undefined") {
      return; // Don't animate if GSAP is not available
    }

    // Call appropriate animation based on device type
    if (isMobile) {
      animateMobile();
    } else {
      animateDesktop();
    }
  }

  function waitForPreloaderFinish() {
    // Check if animation has already been triggered globally
    if (hasAnimatedGlobally) {
      return; // Animation already ran, don't set up listeners again
    }

    // Fallback: Watch for preloader to be hidden
    const observers = [];
    let timeoutId = null;

    // Wrapper function to ensure animation only runs once globally
    const triggerAnimation = () => {
      if (hasAnimatedGlobally) return;
      hasAnimatedGlobally = true;

      // Clean up all observers and event listeners
      observers.forEach((obs) => obs.disconnect());
      document.removeEventListener("preloaderFinished", triggerAnimation);
      if (timeoutId) clearTimeout(timeoutId);

      animateAfterPreloader();
    };

    // Check both preloader IDs
    const preloader = document.getElementById("preloader");
    const preloaderSection = document.getElementById("preloader-section");

    // Check if preloader is already hidden/removed
    const isPreloaderHidden =
      (!preloader ||
        preloader.classList.contains("hidden") ||
        !document.body.contains(preloader)) &&
      (!preloaderSection ||
        preloaderSection.classList.contains("revealed") ||
        preloaderSection.style.display === "none" ||
        !document.body.contains(preloaderSection));

    // If no preloader exists at all, animate immediately
    if (!preloader && !preloaderSection) {
      // Small delay to ensure DOM is ready
      setTimeout(triggerAnimation, 100);
      return;
    }

    if (isPreloaderHidden) {
      // Preloader already finished, animate immediately
      setTimeout(triggerAnimation, 100);
      return;
    }

    // Listen for preloader finish event
    document.addEventListener("preloaderFinished", triggerAnimation, {
      once: true,
    });

    if (preloader) {
      const observer1 = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            if (preloader.classList.contains("hidden")) {
              observer1.disconnect();
              triggerAnimation();
            }
          }
        });
      });
      observer1.observe(preloader, {
        attributes: true,
        attributeFilter: ["class"],
      });
      observers.push(observer1);
    }

    if (preloaderSection) {
      const observer2 = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            (mutation.attributeName === "class" ||
              mutation.attributeName === "style")
          ) {
            if (
              preloaderSection.classList.contains("revealed") ||
              preloaderSection.style.display === "none"
            ) {
              observer2.disconnect();
              triggerAnimation();
            }
          }
        });
      });
      observer2.observe(preloaderSection, {
        attributes: true,
        attributeFilter: ["class", "style"],
      });
      observers.push(observer2);
    }

    // Fallback timeout (in case preloader doesn't trigger event)
    timeoutId = setTimeout(() => {
      observers.forEach((obs) => obs.disconnect());
      triggerAnimation();
    }, 10000);
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeroSection);
} else {
  initHeroSection();
}

// Re-initialize on section load (Shopify theme editor)
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.querySelector(".section-hero")) {
      initHeroSection();
    }
  });
}
